import React from "react";
import SVG from "react-inlinesvg";

import TrustPilotWidgetFooter from "./TrustPilot/TrustPilotWidgetFooter";
import useApiSwr from "../hooks/useApiSwr";

import logo from "../shapes/kitchenadvisor/kitchenadvisor-white.svg";
import messenger from "../shapes/social_icons/messenger-b.svg";
import whatsapp from "../shapes/social_icons/whatsapp-b.svg";
import FooterNewsletter from "./FooterNewsletter";
import FooterFollowUs from "./FooterFollowUs";
import { getLanguage } from "../translations/i18n";

function Footer({ light }) {
  if (light || getLanguage() !== "de") return <FooterLight />;
  else return <FooterFull />;
}

function FooterLight() {
  function A({ href, children }) {
    return (
      <a className={" t-text-base t-text-white hover:t-text-white t-font-avenirLight"} href={href}>
        {children}
      </a>
    );
  }

  function Sep() {
    return <span className="t-text-white t-inline-block t-mx-4">|</span>;
  }

  return (
    <div className="t-bg-primary-500 t-text-white t-text-center t-pt-8 t-pb-4">
      <div className="t-container">
        <SVG
          src={logo}
          className="t-h-8 t-mb-2"
          alt="KitchenAdvisor"
          title="KitchenAdvisor"
          uniquifyIDs
          uniqueHash={"flogo"}
        />

        <div className="t-mb-8">
          <A href="/impressum/">Impressum</A>
          <Sep />
          <A href="/datenschutz/">Datenschutz</A>
          {getLanguage() !== "de" && (
            <>
              <Sep />
              <A href="/">Zur Deutschen Seite</A>
            </>
          )}
        </div>

        <div className={"t-text-white t-text-sm"}>
          © {new Date().getFullYear() !== 2016 ? `2016 - ${new Date().getFullYear()}` : new Date().getFullYear()}{" "}
          KitchenAdvisor
        </div>
      </div>
    </div>
  );
}

function FooterFull() {
  const { data: trustpilotData } = useApiSwr("trustpilotReviews");

  return (
    <div>
      <div className="t-bg-gray-200 t-pt-10 t-pb-1 t-border-0 t-border-solid t-border-b t-border-white">
        <div className="t-container">
          <FooterNewsletter />
          <FooterFollowUs />
        </div>
      </div>
      <Top />
      <Middle />
      <Bottom trustpilotData={trustpilotData} />
    </div>
  );
}

function A({ className = "", href, children }) {
  return (
    <a className={className + " t-text-sm t-text-gray-900 hover:t-text-gray-900 t-font-avenirMedium"} href={href}>
      {children}
    </a>
  );
}

function H({ children }) {
  return <div className=" t-text-sm t-font-bold t-font-avenirMedium t-uppercase t-mb-4">{children}</div>;
}

function Top() {
  const categories = useCategories();

  function CategoryCol({ category }) {
    if (!category) return null;

    return (
      <div>
        <H>{category.categoryName}</H>
        {category?.links?.map(link => (
          <A className="t-block t-text-gray-900 t-mb-3" key={link.title} href={`/${link.item.url}/`}>
            {link.title}
          </A>
        ))}
      </div>
    );
  }

  function SocialCol() {
    return (
      <div>
        <H>KONTAKTIERE UNS</H>
        <div className={"t-flex t-mb-4"}>
          <SVG src={whatsapp} className="t-h-4" />
          <A
            href="https://wa.me/4940999994312"
            target="_blank"
            rel="noopener noreferrer"
            alt="https://wa.me/4940999994312"
            title="https://wa.me/4940999994312"
          >
            <span className="t-block">WhatsApp</span>
            <span>+4940999994312</span>
          </A>
        </div>

        <div className={"t-flex"}>
          <SVG src={messenger} className="t-h-4" />
          <A
            href="https://m.me/kitchenadvisor.de/"
            target="_blank"
            rel="noopener noreferrer"
            alt="https://m.me/kitchenadvisor.de/"
            title="https://m.me/kitchenadvisor.de/"
          >
            <span className="t-block">Facebook Messenger</span>
            <span>m.me/kitchenadvisor.de</span>
          </A>
        </div>
      </div>
    );
  }

  function Col({ children }) {
    return <div className=" t-mb-4 md:t-mb-0">{children}</div>;
  }

  return (
    <div className="t-pl-10 md:t-pl-0 t-bg-gray-200 t-py-12">
      <div className="t-container t-flex t-flex-col md:t-flex-row t-justify-between">
        <Col>
          <TrustPilotWidgetFooter />
        </Col>
        <Col>
          <CategoryCol category={categories.main} />
        </Col>
        <Col>
          <CategoryCol category={categories.top} />
        </Col>

        <Col>
          <SocialCol />
        </Col>
      </div>
    </div>
  );
}

function Middle() {
  const categories = useCategories();

  return (
    <div className="t-container">
      <div className=" t-pl-10 md:t-pl-0 t-py-12  t-font-avenirMedium t-text-gray-900">
        {categories.bottom.map(category => (
          <div key={category.categoryName} className="t-mb-6">
            <H className="t-uppercase t-mb-2">{category.categoryName}</H>

            {category.links.map(link => (
              <A
                className=" t-inline-block t-mr-2 t-text-gray-900 t-text-sm"
                key={link.title}
                href={`/${link.item.url}/`}
              >
                {link.title}
              </A>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Bottom({ trustpilotData }) {
  return (
    <div className="t-bg-primary-600 t-text-gray-400 t-text-center t-pt-8 t-pb-4">
      <div className="t-container">
        <SVG
          src={logo}
          className="t-h-8 t-mb-2"
          alt="KitchenAdvisor"
          title="KitchenAdvisor"
          uniquifyIDs
          uniqueHash={"flogo"}
        />

        <div className={"t-text-white t-mb-4"}>
          © {new Date().getFullYear() !== 2016 ? `2016 - ${new Date().getFullYear()}` : new Date().getFullYear()}{" "}
          KitchenAdvisor
        </div>

        <div className="t-opacity-75 t-text-2xs md:t-text-xs">
          {trustpilotData && (
            <div className={""}>
              Kitchenadvisor.de ist durchschnittlich mit {trustpilotData.businessUnit.trustScore} von 5 Sternen
              ausgezeichnet, auf Grundlage von {trustpilotData.businessUnit.numberOfReviews.total}{" "}
              <a
                className="t-text-gray-400"
                href="https://de.trustpilot.com/review/kitchenadvisor.de"
                rel="nofollow noopener"
                target="_blank"
              >
                Trustpilot
              </a>
              -Anbieterbewertungen.
            </div>
          )}

          <div className="  ">
            Dein Küchenplaner online. Wir beraten individuell und empfehlen dir das perfekte Küchenstudio vor Ort.
          </div>
        </div>
      </div>
    </div>
  );
}

function useCategories() {
  const { data: categories = [] } = useApiSwr("contentfulFooter", {});

  const mainCategory = {
    categoryName: "Mehr über uns",
    links: [
      {
        title: "FAQ",
        item: {
          url: "faq",
        },
      },
      {
        title: "So funktioniert’s",
        item: {
          url: "so-funktioniert-es",
        },
      },
      {
        title: "Jobs bei KitchenAdvisor",
        item: {
          url: "karriere",
        },
      },
      {
        title: "Impressum",
        item: {
          url: "impressum",
        },
      },
      {
        title: "Datenschutz",
        item: {
          url: "datenschutz",
        },
      },
      {
        title: "Händler-Registrierung",
        item: {
          url: "haendler",
        },
      },
      {
        title: "Kitchen Planner (EN)",
        item: {
          url: "en",
        },
      },
    ],
  };

  let topCategories = categories?.filter(c => c.categoryName === "Beliebte Kategorien") || [];
  if (topCategories && topCategories.length > 0) {
    topCategories = topCategories.map(tc => {
      tc.links = tc.links.map(m => {
        if (m.item && m.item.contentType === "manufacturerFronts" && m.item.url === "") {
          m.item.url = "kuechenfronten";
          m.title = "Küchenfronten Übersicht";
        }
        return m;
      });
      return tc;
    });
  }
  const bottomCategories =
    categories
      ?.filter(c => c.categoryName !== "Beliebte Kategorien" && c.categoryName !== "Küchen in Städten")
      .sort((c1, c2) => c1.position > c2.position) || [];

  return { bottom: bottomCategories, top: topCategories?.[0] || {}, main: mainCategory };
}

export default Footer;
